/**
 * API Error Manager
 *
 * Centralized service for managing API error notifications with smart deduplication.
 * Prevents error spam by grouping similar errors within a time window.
 *
 * Features:
 * - Deduplicates errors by endpoint, method, and status code
 * - Normalizes URLs (removes IDs, query params) for grouping
 * - Time-based window to allow re-showing after delay
 * - Automatic cleanup of stale entries
 *
 * @see .github/copilot-instructions.md#api--services API patterns
 */

interface ErrorConfig {
  url: string;
  status: number;
  method?: string;
}

class ApiErrorManager {
  private errorCache = new Map<string, number>(); // errorKey -> timestamp
  private readonly DEDUPE_WINDOW = 5000; // 5 seconds - don't show same error within this window
  private readonly CLEANUP_INTERVAL = 10000; // 10 seconds - cleanup stale entries
  private cleanupTimer: number | null = null;

  constructor() {
    this.startCleanup();
  }

  /**
   * Generate unique key for error grouping
   * Normalizes URL to group similar endpoints together
   *
   * Examples:
   * - /teams/123 -> GET_/teams/:id_404
   * - /teams/456 -> GET_/teams/:id_404 (same key)
   * - /teams?page=2 -> GET_/teams_404
   */
  private getErrorKey(config: ErrorConfig): string {
    // Normalize URL: remove numeric IDs and query params
    const normalizedUrl = config.url
      .replace(/\/\d+/g, '/:id') // /teams/123 -> /teams/:id
      .replace(/\/[a-f0-9-]{36}/gi, '/:uuid') // UUID paths
      .split('?')[0]; // Remove query params

    return `${config.method || 'GET'}_${normalizedUrl}_${config.status}`;
  }

  /**
   * Check if error should be shown to user
   * Returns true if error hasn't been shown recently
   */
  shouldShowError(config: ErrorConfig): boolean {
    const key = this.getErrorKey(config);
    const now = Date.now();
    const lastSeen = this.errorCache.get(key);

    // If not seen recently or never seen, show it
    if (!lastSeen || now - lastSeen > this.DEDUPE_WINDOW) {
      this.errorCache.set(key, now);
      return true;
    }

    // Already shown recently, skip to prevent spam
    return false;
  }

  /**
   * Manually clear error cache (useful for testing or reset scenarios)
   */
  clearCache(): void {
    this.errorCache.clear();
  }

  /**
   * Remove stale entries from cache
   * Called automatically on interval
   */
  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.errorCache.forEach((timestamp, key) => {
      if (now - timestamp > this.DEDUPE_WINDOW) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.errorCache.delete(key));
  }

  /**
   * Start automatic cleanup interval
   */
  private startCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    this.cleanupTimer = setInterval(
      () => this.cleanup(),
      this.CLEANUP_INTERVAL,
    ) as unknown as number;
  }

  /**
   * Stop cleanup interval (useful for testing or cleanup)
   */
  stopCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }
}

// Export singleton instance
export const apiErrorManager = new ApiErrorManager();
